/**
 * Global Price Cache Service - Optimized for Speed
 * Caches futures contract prices with immediate cache return + background refresh
 * Configuration is fetched from backend and can be changed without redeployment
 */
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default configuration - will be overridden by backend config
let CONFIG = {
  CACHE_DURATION_MS: 10000,        // 10 seconds during market hours
  CACHE_DURATION_CLOSED_MS: 300000, // 5 minutes after market hours
  STALE_WHILE_REVALIDATE_MS: 15000, // Serve stale data while refreshing
  REFRESH_INTERVAL_MS: 15000,       // Polling interval
};

// Global price cache with per-symbol timestamps
let priceCache = {}; // { "NSE-NIFTY": { price: 123, timestamp: Date.now() } }
let fetchInProgress = false;
let fetchPromise = null;
let pendingContracts = []; // Queue of contracts waiting to be fetched
let configLoaded = false;

// Symbol name mapping for index futures
const SYMBOL_MAPPING = {
  "NIFTY 50": "NIFTY",
  "NIFTY BANK": "BANKNIFTY",
  "NIFTY FIN SERVICE": "FINNIFTY",
  "BANK NIFTY": "BANKNIFTY",
  "FIN NIFTY": "FINNIFTY",
  "MIDCAP NIFTY": "MIDCPNIFTY",
};

// MCX commodities
const MCX_SYMBOLS = [
  "GOLD", "GOLDM", "GOLDPETAL", "SILVER", "SILVERM", "SILVERMIC",
  "COPPER", "CRUDEOIL", "CRUDEOILM", "NATURALGAS", "ZINC", "ALUMINIUM"
];

/**
 * Load configuration from backend
 */
export const loadConfig = async () => {
  try {
    const response = await api.get("/price-config");
    if (response.data.status && response.data.data) {
      const serverConfig = response.data.data;
      CONFIG.CACHE_DURATION_MS = serverConfig.frontend_cache_duration_ms || 10000;
      CONFIG.REFRESH_INTERVAL_MS = serverConfig.frontend_refresh_interval_ms || 15000;
      CONFIG.STALE_WHILE_REVALIDATE_MS = CONFIG.CACHE_DURATION_MS * 1.5;
      configLoaded = true;
      console.log("PriceService config loaded:", CONFIG);
    }
  } catch (error) {
    console.warn("Failed to load price config, using defaults:", error.message);
  }
  return CONFIG;
};

/**
 * Get current configuration
 */
export const getConfig = () => CONFIG;

/**
 * Get the API symbol from display symbol
 */
export const getApiSymbol = (symbol) => {
  return SYMBOL_MAPPING[symbol] || symbol.replace(/\s+/g, "");
};

/**
 * Get exchange for a symbol
 */
export const getExchangeForSymbol = (symbol) => {
  const upperSymbol = (symbol || "").toUpperCase();
  return MCX_SYMBOLS.some(mcx => upperSymbol.includes(mcx)) ? "MCX" : "NSE";
};

/**
 * Check if market is open
 * Uses backend market-status API for accurate detection (includes holidays).
 * Falls back to time-based check if API unavailable.
 */
let _marketStatusCache = {};
let _marketStatusTimestamp = 0;
const MARKET_STATUS_CACHE_MS = 60000; // Cache for 1 minute

export const isMarketOpen = (exchange = "NSE") => {
  // Use cached status if fresh
  const cached = _marketStatusCache[exchange];
  if (cached !== undefined && Date.now() - _marketStatusTimestamp < MARKET_STATUS_CACHE_MS) {
    return cached;
  }
  
  // Fallback: simple time-based check
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istTime = new Date(now.getTime() + istOffset);
  
  const day = istTime.getUTCDay();
  if (day === 0 || day === 6) return false;
  
  const hours = istTime.getUTCHours();
  const minutes = istTime.getUTCMinutes();
  const timeInMinutes = hours * 60 + minutes;
  
  if (exchange === "MCX") {
    // MCX: Morning 9:00 - 17:00 OR Evening 17:00 - 23:30
    return (timeInMinutes >= 540 && timeInMinutes <= 1020) || (timeInMinutes >= 1020 && timeInMinutes <= 1410);
  }
  
  // NSE/NFO: 9:15 - 15:30
  return timeInMinutes >= 555 && timeInMinutes <= 930;
};

/**
 * Fetch and cache market status from backend (call periodically)
 */
export const refreshMarketStatus = async () => {
  try {
    const response = await api.get("/market-status/all");
    if (response.data.status && response.data.data) {
      const data = response.data.data;
      _marketStatusCache = {};
      for (const [ex, status] of Object.entries(data)) {
        _marketStatusCache[ex] = status.open;
      }
      _marketStatusTimestamp = Date.now();
      return data;
    }
  } catch (error) {
    console.warn("Failed to fetch market status:", error.message);
  }
  return null;
};

/**
 * Get cache duration based on market status
 */
const getCacheDuration = () => {
  return isMarketOpen() ? CONFIG.CACHE_DURATION_MS : CONFIG.CACHE_DURATION_CLOSED_MS;
};

/**
 * Check if a cached price is fresh
 */
const isCacheFresh = (cacheEntry) => {
  if (!cacheEntry || !cacheEntry.timestamp) return false;
  return Date.now() - cacheEntry.timestamp < getCacheDuration();
};

/**
 * Check if cached price is within stale-while-revalidate window
 */
const isCacheUsable = (cacheEntry) => {
  if (!cacheEntry || !cacheEntry.timestamp) return false;
  return Date.now() - cacheEntry.timestamp < CONFIG.STALE_WHILE_REVALIDATE_MS;
};

/**
 * Get cached price for a symbol
 */
export const getCachedPrice = (exchange, symbol) => {
  const key = `${exchange}-${symbol}`;
  const entry = priceCache[key];
  return entry?.price;
};

/**
 * Get all cached prices (just the price values)
 */
export const getAllCachedPrices = () => {
  const result = {};
  for (const [key, entry] of Object.entries(priceCache)) {
    if (entry?.price !== undefined) {
      result[key] = entry.price;
    }
  }
  return result;
};

/**
 * Clear the cache
 */
export const clearPriceCache = () => {
  priceCache = {};
};

/**
 * Fetch prices for multiple contracts
 * Strategy: Return cached data immediately, refresh stale data in background
 * 
 * @param {Array} contracts - Array of {symbol, exchange, contract_expiry}
 * @param {boolean} forceRefresh - Force refresh even if cache is valid
 * @returns {Object} - Map of "exchange-symbol" -> price
 */
export const fetchContractPrices = async (contracts, forceRefresh = false) => {
  if (!contracts || contracts.length === 0) {
    return getAllCachedPrices();
  }

  // Normalize contracts with proper exchange
  const normalizedContracts = contracts.map(c => ({
    symbol: c.symbol,
    exchange: c.exchange || getExchangeForSymbol(c.symbol),
    contract_expiry: c.contract_expiry || null
  }));

  // Separate contracts into fresh, stale, and missing
  const freshPrices = {};
  const contractsToFetch = [];

  for (const contract of normalizedContracts) {
    const key = `${contract.exchange}-${contract.symbol}`;
    const cacheEntry = priceCache[key];

    if (!forceRefresh && isCacheFresh(cacheEntry)) {
      // Fresh cache - use it
      freshPrices[key] = cacheEntry.price;
    } else if (isCacheUsable(cacheEntry)) {
      // Stale but usable - return it but also refresh
      freshPrices[key] = cacheEntry.price;
      contractsToFetch.push(contract);
    } else {
      // Missing or too old - must fetch
      contractsToFetch.push(contract);
    }
  }

  // If everything is fresh, return immediately
  if (contractsToFetch.length === 0) {
    return getAllCachedPrices();
  }

  // If we have some cached prices, return them immediately and fetch in background
  if (Object.keys(freshPrices).length > 0 && !forceRefresh) {
    // Start background fetch without waiting
    _queueFetch(contractsToFetch);
    return getAllCachedPrices();
  }

  // No cached prices available - must wait for fetch
  await _doFetchPrices(contractsToFetch);
  return getAllCachedPrices();
};

/**
 * Queue contracts for batch fetching
 */
const _queueFetch = (contracts) => {
  // Add to pending queue
  pendingContracts.push(...contracts);
  
  // Debounce - wait briefly for more contracts to queue up
  if (!fetchInProgress) {
    setTimeout(() => {
      if (pendingContracts.length > 0) {
        const toFetch = [...new Set(pendingContracts.map(c => JSON.stringify(c)))].map(s => JSON.parse(s));
        pendingContracts = [];
        _doFetchPrices(toFetch);
      }
    }, 50); // Reduced from 100ms to 50ms for faster response
  }
};

/**
 * Internal function to fetch prices from API
 */
const _doFetchPrices = async (contracts) => {
  if (!contracts || contracts.length === 0) return;
  
  // Prevent concurrent fetches
  if (fetchInProgress && fetchPromise) {
    await fetchPromise;
    return;
  }

  fetchInProgress = true;
  
  const doFetch = async () => {
    try {
      // Deduplicate contracts
      const uniqueContracts = [];
      const seen = new Set();
      for (const c of contracts) {
        const key = `${c.exchange}-${c.symbol}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueContracts.push(c);
        }
      }

      // Build batch request
      const batchContracts = uniqueContracts.map(c => ({
        symbol: getApiSymbol(c.symbol),
        exchange: c.exchange,
        contract_expiry: c.contract_expiry || null
      }));

      const response = await api.post("/contract-prices-batch", { 
        contracts: batchContracts 
      }, {
        timeout: 30000 // 30 second timeout
      });

      if (response.data.status && response.data.data) {
        const now = Date.now();
        response.data.data.forEach((result, index) => {
          const originalContract = uniqueContracts[index];
          if (result.ltp !== null && result.ltp !== undefined) {
            const key = `${originalContract.exchange}-${originalContract.symbol}`;
            priceCache[key] = {
              price: result.ltp,
              timestamp: now,
              contractSymbol: result.contract_symbol,
              contractExpiry: result.contract_expiry
            };
          }
        });
      }
    } catch (error) {
      console.error("PriceService: Error fetching prices:", error?.response?.data?.message || error.message);
    }
  };

  fetchPromise = doFetch();
  
  try {
    await fetchPromise;
  } finally {
    fetchInProgress = false;
    fetchPromise = null;
  }
};

/**
 * Pre-warm cache with a list of symbols
 * Call this on app initialization
 */
export const prewarmCache = async (contracts) => {
  if (!contracts || contracts.length === 0) return;
  await _doFetchPrices(contracts);
};

/**
 * Subscribe to price updates (poll at interval)
 * Uses configurable interval from backend settings
 * Returns cleanup function
 */
export const subscribeToPriceUpdates = (contracts, onUpdate, intervalMs = null) => {
  let active = true;
  
  // Use config interval if not specified
  const baseInterval = intervalMs || CONFIG.REFRESH_INTERVAL_MS;
  
  const poll = async () => {
    if (!active) return;
    
    const prices = await fetchContractPrices(contracts, true);
    if (active && onUpdate) {
      onUpdate(prices);
    }
  };
  
  // Initial fetch
  poll();
  
  // Set up interval - poll more frequently during market hours
  const getInterval = () => isMarketOpen() ? baseInterval : baseInterval * 2;
  
  let intervalId = setInterval(poll, getInterval());
  
  // Adjust interval when market status changes (check every minute)
  const checkInterval = setInterval(() => {
    clearInterval(intervalId);
    intervalId = setInterval(poll, getInterval());
  }, 60000);
  
  // Return cleanup function
  return () => {
    active = false;
    clearInterval(intervalId);
    clearInterval(checkInterval);
  };
};

export default {
  fetchContractPrices,
  getCachedPrice,
  getAllCachedPrices,
  clearPriceCache,
  subscribeToPriceUpdates,
  prewarmCache,
  getApiSymbol,
  getExchangeForSymbol,
  isMarketOpen,
  refreshMarketStatus,
  loadConfig,
  getConfig
};
